import styled from "styled-components";
import {motion} from "framer-motion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {container, down, focusRing, glass, up} from "../../../Styles/media";

const Header = styled.header<{ $scrolled: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 900;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid ${({theme, $scrolled}) => ($scrolled ? theme.line : 'transparent')};
    transition: background-color 320ms ${({theme}) => theme.ease.out},
    border-color 320ms ${({theme}) => theme.ease.out},
    box-shadow 320ms ${({theme}) => theme.ease.out};

    ${({$scrolled}) => $scrolled && glass};
    box-shadow: ${({theme, $scrolled}) => ($scrolled ? theme.shadow.sm : 'none')};
`

const Bar = styled.div`
    ${container};
    display: flex;
    align-items: center;
    gap: 18px;
    height: ${({theme}) => theme.header.height}px;

    /*
     * Below the desktop nav breakpoint the bar becomes three tracks —
     * burger | logo | actions — so the wordmark is centred on the viewport
     * regardless of how wide the two side clusters happen to be.
     */
    ${down('lg')} {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        gap: 8px;
    }

    ${down('md')} {
        height: ${({theme}) => theme.header.heightMobile}px;
    }
`

const IconButton = styled.button`
    ${focusRing};
    display: none;
    justify-self: start;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    margin-left: -8px;
    border-radius: ${({theme}) => theme.radius.sm};
    color: ${({theme}) => theme.color};
    transition: background-color 200ms ${({theme}) => theme.ease.out};

    &:hover {
        background-color: rgba(255, 255, 255, 0.07);
    }

    ${down('lg')} {
        display: flex;
    }
`

const BurgerIcon = styled(FontAwesomeIcon)`
    width: 20px;
    height: 20px;
`

const Logo = styled.button`
    ${focusRing};
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding: 0 2px;
    font-family: ${({theme}) => theme.font.logo};
    font-size: 40px;
    line-height: 1;
    letter-spacing: 0.02em;
    color: ${({theme}) => theme.color};
    transition: color 240ms ${({theme}) => theme.ease.out}, opacity 240ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme}) => theme.accent};
    }

    ${down('lg')} {
        justify-self: center;
    }

    ${down('md')} {
        font-size: 32px;
    }
`

const Nav = styled.nav`
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: 24px;

    ${down('lg')} {
        display: none;
    }
`

const NavItem = styled.button<{ $active: boolean }>`
    ${focusRing};
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 14px;
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: ${({theme, $active}) => ($active ? theme.color : theme.colorMuted)};
    transition: color 220ms ${({theme}) => theme.ease.out}, background-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme}) => theme.color};
        background-color: rgba(255, 255, 255, 0.06);
    }
`

const NavItemDot = styled.span<{ $active: boolean }>`
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${({theme}) => theme.accent};
    opacity: ${({$active}) => ($active ? 1 : 0)};
    transform: scale(${({$active}) => ($active ? 1 : 0.4)});
    transition: opacity 240ms ${({theme}) => theme.ease.out}, transform 240ms ${({theme}) => theme.ease.out};
`

const RightGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;

    ${down('lg')} {
        justify-self: end;
    }
`

const LanguageSwitch = styled.div`
    display: flex;
    align-items: center;
    padding: 3px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.pill};

    ${down('sm')} {
        display: none;
    }
`

const LanguageOption = styled.button<{ $active: boolean }>`
    ${focusRing};
    padding: 4px 11px;
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({theme, $active}) => ($active ? theme.accentContrast : theme.colorMuted)};
    background-color: ${({theme, $active}) => ($active ? theme.color : 'transparent')};
    transition: color 200ms ${({theme}) => theme.ease.out}, background-color 200ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme, $active}) => ($active ? theme.accentContrast : theme.color)};
    }
`

const CallLink = styled.a`
    ${focusRing};
    display: none;
    align-items: center;
    gap: 9px;
    height: 40px;
    padding: 0 16px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: ${({theme}) => theme.radius.pill};
    font-size: 13px;
    font-weight: 600;
    color: ${({theme}) => theme.color};
    transition: border-color 220ms ${({theme}) => theme.ease.out}, background-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        border-color: ${({theme}) => theme.accentLine};
        background-color: ${({theme}) => theme.accentSoft};
    }

    ${up('xl')} {
        display: flex;
    }
`

const CallIcon = styled(FontAwesomeIcon)`
    width: 13px;
    height: 13px;
    color: ${({theme}) => theme.accent};
`

const BasketButton = styled.button`
    ${focusRing};
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    height: 42px;
    padding: 0 16px;
    border-radius: ${({theme}) => theme.radius.pill};
    background-color: ${({theme}) => theme.color};
    color: ${({theme}) => theme.colorInverted};
    font-size: 13px;
    font-weight: 700;
    transition: transform 220ms ${({theme}) => theme.ease.out}, background-color 220ms ${({theme}) => theme.ease.out};

    &:hover {
        transform: translateY(-1px);
        background-color: ${({theme}) => theme.accent};
        color: ${({theme}) => theme.accentContrast};
    }

    ${down('md')} {
        padding: 0;
        width: 42px;
        justify-content: center;
        background-color: transparent;
        color: ${({theme}) => theme.color};

        &:hover {
            background-color: rgba(255, 255, 255, 0.07);
            color: ${({theme}) => theme.color};
        }
    }
`

const BasketIcon = styled(FontAwesomeIcon)`
    width: 16px;
    height: 16px;
`

const BasketLabel = styled.span`
    ${down('md')} {
        display: none;
    }
`

const BasketBubble = styled(motion.span)`
    position: absolute;
    top: -3px;
    right: -3px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 21px;
    height: 21px;
    padding: 0 5px;
    border-radius: ${({theme}) => theme.radius.pill};
    border: 2px solid ${({theme}) => theme.backgroundColor};
    background-color: ${({theme}) => theme.accent};
    color: ${({theme}) => theme.accentContrast};
    font-size: 11px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    line-height: 1;
`

export const Styled = {
    Header,
    Bar,
    IconButton,
    BurgerIcon,
    Logo,
    Nav,
    NavItem,
    NavItemDot,
    RightGroup,
    LanguageSwitch,
    LanguageOption,
    CallLink,
    CallIcon,
    BasketButton,
    BasketIcon,
    BasketLabel,
    BasketBubble
}
